"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";

interface ScrollablePillRowProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * A row that scrolls horizontally on mobile with minimalist chevron indicators.
 * On desktop (lg+), it wraps normally.
 */
export function ScrollablePillRow({ children, className = "" }: ScrollablePillRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Initial check
    requestAnimationFrame(updateScrollState);

    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState, { passive: true });

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  // Re-check when children change
  useEffect(() => {
    requestAnimationFrame(updateScrollState);
  }, [children, updateScrollState]);

  const scrollBy = (amount: number) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <div className={`relative ${className}`}>
      {/* Left chevron - mobile only */}
      {canScrollLeft && (
        <button
          onClick={() => scrollBy(-150)}
          className="absolute left-0 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-sm lg:hidden"
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-4 w-4 text-gray-600" />
        </button>
      )}

      {/* Scrollable container - horizontal on mobile, wrap on desktop */}
      <div
        ref={scrollRef}
        className="flex flex-nowrap items-start gap-[5.246px] overflow-x-auto [&::-webkit-scrollbar]:hidden lg:flex-wrap lg:overflow-visible"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          opacity: 0.8,
        }}
      >
        {children}
      </div>

      {/* Right chevron - mobile only */}
      {canScrollRight && (
        <button
          onClick={() => scrollBy(150)}
          className="absolute right-0 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-sm lg:hidden"
          aria-label="Scroll right"
        >
          <ChevronRight className="h-4 w-4 text-gray-600" />
        </button>
      )}
    </div>
  );
}
